import React, { useState, useCallback, useContext, ReactNode } from 'react'
import {
  SplineLoadingContext,
  SplineLoadingContextType,
  SplineLoadingState,
  initialSplineLoadingState,
} from '@contexts/spline-loading-context'

export const SplineLoadingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<SplineLoadingState>(initialSplineLoadingState)

  const registerIframe = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      totalIframes: prev.totalIframes + 1,
      loadingStatus: {
        ...prev.loadingStatus,
        [id]: false,
      },
      isAllLoaded: false,
    }))
  }, [])

  const markIframeLoaded = useCallback((id: string) => {
    setState((prev) => {
      const newLoadingStatus = {
        ...prev.loadingStatus,
        [id]: true,
      }
      const newLoadedCount = Object.values(newLoadingStatus).filter(Boolean).length
      const isAllLoaded = newLoadedCount === prev.totalIframes && prev.totalIframes > 0

      return {
        ...prev,
        loadedIframes: newLoadedCount,
        loadingStatus: newLoadingStatus,
        isAllLoaded,
      }
    })
  }, [])

  const resetState = useCallback(() => {
    setState(initialSplineLoadingState)
  }, [])

  const contextValue: SplineLoadingContextType = {
    state,
    registerIframe,
    markIframeLoaded,
    resetState,
  }

  return <SplineLoadingContext.Provider value={contextValue}>{children}</SplineLoadingContext.Provider>
}

export const useSplineLoading = () => {
  const context = useContext(SplineLoadingContext)
  if (context === undefined) {
    throw new Error('useSplineLoading must be used within a SplineLoadingProvider')
  }
  return context
}

// Hook specifically for components that want to wait for Spline loading
export const useWaitForSplineLoading = () => {
  const { state } = useSplineLoading()
  return {
    isSplineReady: state.isAllLoaded || state.totalIframes === 0, // Ready if all loaded or no iframes
    splineLoadingProgress: state.totalIframes > 0 ? state.loadedIframes / state.totalIframes : 1,
    totalIframes: state.totalIframes,
    loadedIframes: state.loadedIframes,
  }
}
